import React, { useState, useEffect, useCallback } from 'react';
import { getUsuariosPaginados, deleteUsuario } from '../services/usuarioService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, UserCheck, Mail, Shield } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function UsuariosList() {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarUsuarios = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getUsuariosPaginados(page, pageSize, debouncedSearch);
            setUsuarios(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
            setError('No se pudieron cargar los usuarios.');
            toast.error('Error al cargar los usuarios');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarUsuarios();
    }, [cargarUsuarios]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
            try {
                await deleteUsuario(id);
                toast.success('Usuario eliminado exitosamente');
                cargarUsuarios();
            } catch (error) {
                console.error('Error al eliminar:', error);
                toast.error('No se pudo eliminar el usuario.');
            }
        }
    };

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/seguridad')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver al Panel de Seguridad"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-green-400" />
                        <h1 className="text-lg font-semibold tracking-tight">Seguridad: Gestión de Usuarios</h1>
                    </div>
                </div>

                <Link
                    to="/seguridad/usuarios/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVO USUARIO
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Mostrar:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-gray-300 p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm disabled:opacity-50"
                        disabled={loading}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, correo o usuario..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-72 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm bg-white"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative min-h-[400px]">
                {error && (
                    <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-xs uppercase tracking-wider font-bold">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 text-center">ID</th>
                                <th className="px-4 py-3 border-r border-gray-200">Operador / Cuenta</th>
                                <th className="px-4 py-3 border-r border-gray-200">E-mail</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-48">Roles</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-24 text-center">Estado</th>
                                <th className="px-4 py-3 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-600">
                            {loading && usuarios.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                                            <span className="text-blue-600 font-medium">Sincronizando seguridad...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : usuarios.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-12 text-center text-gray-500 flex flex-col items-center gap-2">
                                        <Search className="w-8 h-8 text-gray-300" />
                                        <span className="italic">
                                            {searchTerm
                                                ? `No se encontraron resultados para "${searchTerm}"`
                                                : 'No hay usuarios registrados en el sistema.'}
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                usuarios.map((usuario) => (
                                    <tr key={usuario.idUsuario} className="border-b border-gray-200 hover:bg-[#f1f5f9] transition-colors group">
                                        <td className="px-4 py-3 border-r border-gray-200 text-center font-mono text-xs text-gray-500">
                                            {usuario.idUsuario}
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-gray-800 uppercase tracking-tight">
                                                    {usuario.nombre} {usuario.apellido}
                                                </span>
                                                <span className="text-[0.7rem] bg-gray-100 text-gray-500 px-1 border border-gray-200 rounded-sm w-fit font-mono">
                                                    @{usuario.nombreUsuario}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200">
                                            <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                                                <Mail className="w-3.5 h-3.5" />
                                                {usuario.email}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200">
                                            <div className="flex flex-wrap gap-1">
                                                {usuario.roles?.map((rol, idx) => (
                                                    <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[0.65rem] font-bold rounded border border-blue-100 uppercase">
                                                        <Shield className="w-2.5 h-2.5" />
                                                        {rol}
                                                    </span>
                                                )) || <span className="text-xs italic text-gray-400">Sin roles</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-[0.65rem] font-bold uppercase ${usuario.estado
                                                ? 'bg-green-100 text-green-700 border border-green-200'
                                                : 'bg-red-100 text-red-700 border border-red-200'
                                                }`}>
                                                {usuario.estado ? 'Activo' : 'Baja'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center items-center gap-3 font-bold text-[0.7rem] tracking-widest uppercase">
                                                <Link
                                                    to={`/seguridad/usuarios/${usuario.idUsuario}/editar`}
                                                    className="text-blue-600 hover:text-blue-800 transition-all"
                                                >
                                                    EDITAR
                                                </Link>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    onClick={() => handleEliminar(usuario.idUsuario)}
                                                    className="text-red-500 hover:text-red-700 transition-all"
                                                >
                                                    ELIMINAR
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginator */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-sm italic text-gray-500">
                <div>
                    Mostrando <span className="text-slate-700 font-bold">{startItem} a {endItem}</span> de {totalElements}
                </div>
                <div className="flex items-center gap-1 italic not-italic">
                    <button onClick={() => setPage(0)} disabled={page === 0 || loading} className="p-1 border bg-white rounded-sm disabled:opacity-30 self-center">
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => setPage(page - 1)} disabled={page === 0 || loading} className="p-1 border bg-white rounded-sm disabled:opacity-30 self-center">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="px-3 py-1 bg-blue-600 text-white font-bold rounded-sm text-xs mx-1">
                        {page + 1}
                    </div>
                    <span className="text-xs text-gray-400">de {totalPages || 1}</span>
                    <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1 || loading} className="p-1 border bg-white rounded-sm disabled:opacity-30 self-center">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1 || totalPages === 0 || loading} className="p-1 border bg-white rounded-sm disabled:opacity-30 self-center">
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
